<template lang="pug" src="./aiEditControls.pug"></template>

<i18n>
{
    "de": {
        "Templates": "Vorlagen",
        "Load more": "Mehr laden",
        "Custom prompt": "Eigener Prompt",
        "This template sends reference images, only models with multi-image input are selectable.": "Diese Vorlage sendet Referenzbilder, es sind nur Modelle mit Mehrbild-Eingabe wählbar.",
        "Model": "Modell",
        "Output size": "Ausgabegröße",
        "Keep source": "Original beibehalten",
        "Keeps the original dimensions.": "Behält die ursprünglichen Abmessungen bei.",
        "The AI renders at this aspect ratio.": "Die KI rendert in diesem Seitenverhältnis.",
        "Landscape": "Querformat",
        "Portrait": "Hochformat",
        "Image edit": "Bildbearbeitung",
        "Image editing, {count} images": "Bildbearbeitung, {count} Bilder",
        "maximum": "maximal",
        "estimated": "geschätzt",
        "Estimate. Billed are the actual generation costs, which can differ.": "Schätzung. Abgerechnet werden die tatsächlichen Generierungskosten, die abweichen können.",
        "up to": "bis zu",
        "Billed are the actual generation costs, at most this amount.": "Abgerechnet werden die tatsächlichen Generierungskosten, höchstens dieser Betrag.",
        "AI preparation": "KI-Vorbereitung",
        "this edit": "diese Bearbeitung",
        "Actual cost of the AI suggestions and prompt prepared for this edit.": "Tatsächliche Kosten der KI-Vorschläge und des Prompts für diese Bearbeitung.",
        "unit.seconds": "Sekunde | Sekunden",
        "unit.images": "Bild | Bilder",
        "Quality": "Qualität",
        "quality.low": "Niedrig",
        "quality.medium": "Mittel",
        "quality.high": "Hoch",
        "Resolution": "Auflösung",
        "Maximum edge length": "Maximale Kantenlänge",
        "Limits the result to at most this size on its longer edge, without upscaling a smaller source.": "Begrenzt das Ergebnis auf höchstens diese Größe an der längeren Kante, ohne eine kleinere Quelle zu vergrößern."
    },
    "en": {
        "unit.seconds": "second | seconds",
        "unit.images": "image | images",
        "Image editing, {count} images": "Image editing, {count} images",
        "quality.low": "Low",
        "quality.medium": "Medium",
        "quality.high": "High"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { moneyFormat, preciseMoneyFormat } from '@/filters';
import api from '@/extensions/generative-media/store/api/generativeMedia';
import ModelSelectOption from '@/extensions/generative-media/components/ModelSelectOption';

/**
 * The shared "what should the AI do" form: prompt templates, model, output size and the
 * cost estimate.
 *
 * Used by the single-image editor drawer and by the media library's bulk dialog, so the
 * two cannot drift apart. Purely presentational - both hosts already fetch their own
 * templates and models, and own the selection state. Props are individual and `.sync`ed
 * rather than one v-model object, so a host can keep its existing data properties
 * untouched and just bind them.
 */
export default {
    name: 'AiEditControls',
    components: { ModelSelectOption },
    props: {
        templates: {
            type: Array,
            default: () => [],
        },
        templatesTotal: {
            type: Number,
            default: 0,
        },
        loadingTemplates: {
            type: Boolean,
            default: false,
        },
        loadingMoreTemplates: {
            type: Boolean,
            default: false,
        },
        models: {
            type: Array,
            default: () => [],
        },
        // Live orchestration cost already incurred (suggestions + prompt phrasing).
        // Only the single-image editor spends this; bulk is template-only.
        orchestrationCost: {
            type: Number,
            default: 0,
        },
        // How many images the estimate covers. 1 in the editor, the selection size in
        // the bulk dialog - the only behavioural difference between the two hosts.
        multiplier: {
            type: Number,
            default: 1,
        },
        // Bulk editing is template-only, so it hides the free-text chip.
        showCustomPrompt: {
            type: Boolean,
            default: false,
        },
        // The editor drawer swaps between a templates tab and a suggestions tab.
        showTemplates: {
            type: Boolean,
            default: true,
        },
        customSelected: {
            type: Boolean,
            default: false,
        },

        templateId: {
            type: Number,
            default: null,
        },
        modelKey: {
            type: String,
            default: null,
        },
        aspect: {
            type: String,
            default: 'source',
        },
        orientation: {
            type: String,
            default: 'landscape',
        },
        // Needed to fetch the ceiling estimate (per-CMS margin + FX). Without it the
        // component falls back to the flat effectivePrice x multiplier figure.
        cmsId: {
            type: [Number, String],
            default: null,
        },
        // Cost levers. Only rendered for a model that actually declares them
        // (see EX_GM_Model.qualityOptions / resolutionOptions / resolutionCapOptions).
        quality: {
            type: String,
            default: null,
        },
        resolution: {
            type: String,
            default: null,
        },
        resolutionCap: {
            type: Number,
            default: null,
        },
        // The source image's longer edge in px, when a single fixed source is known (the
        // single-image editor). Caps at or above it would not shrink anything, so they are
        // filtered out instead of being offered as a no-op choice. Left null in the bulk
        // dialog, where each selected image can be a different size.
        sourceLongEdge: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            // The ceiling is fetched async (it needs a round-trip through FX + margin);
            // the flat estimate is shown immediately so the panel never blanks while it
            // loads, then replaced once the real ceiling comes back.
            fetchedCeiling: null,
            ceilingRequestToken: 0,
        };
    },
    computed: {
        // "source" keeps the original dimensions (the default); the ratios are the set
        // every image-edit model can express natively.
        outputAspectOptions() {
            return [
                { key: 'source', label: 'Keep source' },
                { key: '1:1', label: '1:1' },
                { key: '4:3', label: '4:3' },
                { key: '16:9', label: '16:9' },
            ];
        },
        selectedTemplate() {
            return this.templates.find(template => template.id === this.templateId) || null;
        },
        // A template carrying reference images restricts the model picker to multi-image
        // (image_urls array) models - flux-kontext cannot take them.
        selectedTemplateWithReferences() {
            const template = this.selectedTemplate;
            return !!(template && (template.references || []).length);
        },
        // flux-2-pro bills a per-reference surcharge — feeds the ceiling estimate.
        referenceCount() {
            return _.get(this.selectedTemplate, 'references', []).length;
        },
        availableModels() {
            if (!this.selectedTemplateWithReferences) return this.models;
            return this.models.filter(model => model.supportsReferences);
        },
        selectedModel() {
            return this.models.find(model => model.key === this.modelKey) || null;
        },
        qualityOptions() {
            return _.get(this.selectedModel, 'qualityOptions') || [];
        },
        resolutionOptions() {
            return _.get(this.selectedModel, 'resolutionOptions') || [];
        },
        // Every tier below the source is a real downscale; above that, every tier behaves
        // identically (a no-op, since none of them shrink an already-smaller source) — so
        // only the SMALLEST of those no-op tiers is kept, as the "stays as-is" choice.
        // Dropping every tier >= source instead (an earlier version of this) would ratchet
        // an image smaller on every re-edit: pick 2048 on a 2600px source, and the very next
        // edit would only ever offer 1024, forever, because the output (now 2048px) had
        // shrunk the option list itself. Keeping the boundary tier breaks that ratchet.
        resolutionCapOptions() {
            const options = _.get(this.selectedModel, 'resolutionCapOptions') || [];
            if (!this.sourceLongEdge) return options;
            const smaller = options.filter(option => option < this.sourceLongEdge);
            const noOpTiers = options.filter(option => option >= this.sourceLongEdge);
            // The top tier is always one the source is already within, so it cannot
            // shrink anything - that IS the "don't shrink" choice, expressed as a real
            // size instead of a 0 sentinel. When the source outgrows every configured
            // tier there is none, so offer the source's own edge: the backend rejects
            // it as unoffered and resolves to no cap, which is the same no-shrink run.
            if (noOpTiers.length) return [...smaller, Math.min(...noOpTiers)];
            return [...smaller, this.sourceLongEdge];
        },
        // The flat product (today's behaviour) as an immediate placeholder while the
        // real ceiling loads, and the permanent fallback when it cannot be fetched
        // (no cmsId yet, or the model carries no ceiling table server-side).
        flatEstimate() {
            const price = _.get(this.selectedModel, 'effectivePrice');
            if (!price) return null;
            return price * this.multiplier;
        },
        hasFetchedCeiling() {
            return this.fetchedCeiling !== null;
        },
        estimatedCost() {
            return this.hasFetchedCeiling ? this.fetchedCeiling : this.flatEstimate;
        },
        estimatedCostFormatted() {
            if (!this.estimatedCost) return null;
            return moneyFormat(this.estimatedCost, 'EUR');
        },
        orchestrationCostFormatted() {
            // Actual (proportional) cost - small, so show extra precision rather than
            // rounding sub-cent amounts to 0,00 €.
            return this.preciseCurrency(this.orchestrationCost || 0);
        },
    },
    watch: {
        // Function-form (NOT the string-handler-name form): Vue resolves a string
        // handler via vm[handler] once, during initWatch — which runs BEFORE
        // created(), so a handler assigned in created() (as debouncedFetchCeiling
        // is, below) would be captured as undefined and crash on first fire. A
        // function watcher only reads `this.debouncedFetchCeiling` when it actually
        // fires, by which point created() has long since run.
        modelKey() { this.debouncedFetchCeiling(); },
        // Also re-sync: the host can null a lever at any time (applying a template that
        // pins none of them does exactly that), and null must resolve to the shop/model
        // default rather than leaving every chip unselected. syncAllLeverDefaults is a
        // no-op for a value that IS offered, so this cannot loop.
        quality() { this.syncAllLeverDefaults(); this.debouncedFetchCeiling(); },
        resolution() { this.syncAllLeverDefaults(); this.debouncedFetchCeiling(); },
        resolutionCap() { this.syncAllLeverDefaults(); this.debouncedFetchCeiling(); },
        multiplier() { this.debouncedFetchCeiling(); },
        referenceCount() { this.debouncedFetchCeiling(); },
        cmsId() { this.debouncedFetchCeiling(); },
        // Keeps every lever valid for whichever model is now selected — fires on the
        // initial model too (immediate), not just on a later switch, so a lever is never
        // left unselected just because the host started the value at null.
        selectedModel: {
            immediate: true,
            handler() { this.syncAllLeverDefaults(); },
        },
        // The single-image editor learns the source's dimensions asynchronously (after the
        // image has loaded), which can arrive after the model is already selected — re-sync
        // the resolution-cap lever once it's known, without waiting for another model switch.
        sourceLongEdge() { this.syncAllLeverDefaults(); },
    },
    created() {
        this.debouncedFetchCeiling = _.debounce(this.fetchCeiling, 300);
        this.fetchCeiling();
    },
    beforeDestroy() {
        // A pending debounce would otherwise fire its request after the panel is gone.
        this.debouncedFetchCeiling.cancel();
    },
    methods: {
        // Every unresolved lever change is a new estimate — the ceiling depends on
        // exactly which cell of the price table the resolved values land on.
        async fetchCeiling() {
            const token = ++this.ceilingRequestToken;

            if (!this.cmsId || !this.modelKey || !this.multiplier) {
                this.fetchedCeiling = null;
                return;
            }

            try {
                const ceiling = await api.costCeiling(this.cmsId, this.modelKey, this.multiplier, {
                    quality: this.quality,
                    resolution: this.resolution,
                    resolution_cap: this.resolutionCap,
                }, this.referenceCount);

                // A slower, now-stale request must not overwrite a fresher result.
                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = ceiling;
                }
            } catch (error) {
                if (token === this.ceilingRequestToken) {
                    this.fetchedCeiling = null;
                }
            }
        },
        // Sub-cent amounts shown at full precision. Per-image prices are routinely below
        // a cent (gpt-image-2 is 0.018 €), so rounding the UNIT price to two decimals
        // made the total look wrong: "0,02 €/image" x 4 rendering as 0,07 €. The total
        // itself stays at normal money precision - it is the figure being charged.
        preciseCurrency(value) {
            const language = window.navigator.userLanguage || window.navigator.language;

            return Intl.NumberFormat(language, {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
            }).format(Number(value) || 0);
        },
        // The COLLAPSED select value only — the expanded list renders
        // <model-select-option>, which owns the two-line layout.
        modelLabel(model) {
            const price = preciseMoneyFormat(model.effectivePrice);
            const unit = this.$tc('unit.' + model.unit, 1);
            return `${model.label} · ${this.$t('up to')} ${price}/${unit}`;
        },
        qualityLabel(value) {
            return this.$t('quality.' + value);
        },
        resolutionCapLabel(value) {
            return `${value}px`;
        },
        selectTemplate(template) {
            this.$emit('update:templateId', template.id);
            this.$emit('select-template', template);
        },
        syncAllLeverDefaults() {
            const model = this.selectedModel;
            if (!model) return;
            // Validate against the (possibly source-filtered) rendered option lists, not
            // the model's raw ones — a default the source has filtered out as a no-op
            // must not be "synced" back in as if it were still a real, visible choice.
            this.syncLeverDefault(this.quality, this.qualityOptions, model.qualityDefault, 'update:quality');
            // `resolution` is intentionally not synced: the image-edit mutation has no
            // such argument, so the panel neither renders nor submits it (see the pug).
            //
            // The cap falls back to the LARGEST offered tier, never to nothing: that
            // tier cannot shrink the source, so it is the safe choice when the model's
            // configured default was filtered out as a no-op. Leaving it unselected is
            // what made the control read as broken. Quality gets no such fallback -
            // there the largest option is the most expensive one.
            this.syncLeverDefault(
                this.resolutionCap,
                this.resolutionCapOptions,
                model.resolutionCapDefault,
                'update:resolutionCap',
                _.last(this.resolutionCapOptions),
            );
        },
        // A lever's value must stay one of the CURRENT model's options: falls back to the
        // model's resolved default (EX_GM_Model.*Default — already CMS-aware server-side,
        // see GenerativeMediaService::resolveLeverValue) when it isn't, and clears it when
        // the model has no such lever at all (a value carried over from a previous model
        // would otherwise be silently ignored server-side, but shown as if still active).
        syncLeverDefault(current, options, defaultValue, event, fallback = null) {
            const validOptions = options || [];
            if (!validOptions.length) {
                if (current !== null) this.$emit(event, null);
                return;
            }
            if (!validOptions.includes(current)) {
                this.$emit(event, validOptions.includes(defaultValue) ? defaultValue : fallback);
            }
        },
    },
};
</script>

<style lang="stylus" scoped>
// Hosts set --gm-accent (CMS blue / shop purple). Resolved into a SEPARATE property:
// a custom property cannot reference itself, and doing so makes it invalid - which
// rendered the active button as white text on no background.
.ai-edit-controls
    --ai-accent var(--gm-accent, #409EFF)

.gm-label
    display block
    font-size 13px
    font-weight 600
    color #303133
    margin-bottom 10px

.gm-hint
    font-size 12px
    line-height 1.4
    color #909399
    margin-top -4px
    margin-bottom 10px

    &--below
        margin-top 6px
        margin-bottom 0

.gm-section
    margin-top 20px

.gm-chips
    display flex
    flex-wrap wrap
    gap 8px

.gm-chip-skeleton
    display inline-block
    width 130px
    height 31px
    border-radius 18px
    background linear-gradient(90deg, #f0f0f2 25%, #e6e6ea 37%, #f0f0f2 63%)
    background-size 400% 100%
    animation gm-shimmer 1.4s ease infinite

    &:nth-child(1)
        width 150px
    &:nth-child(2)
        width 110px
    &:nth-child(3)
        width 175px
    &:nth-child(4)
        width 125px
    &:nth-child(5)
        width 95px

@keyframes gm-shimmer
    0%
        background-position 100% 50%
    100%
        background-position 0 50%

.gm-chip
    display inline-flex
    align-items center
    gap 6px
    padding 7px 14px
    border-radius 18px
    border 1px solid #dcdfe6
    background #fff
    color #606266
    font-size 13px
    line-height 1
    cursor pointer
    transition all .15s ease

    &:hover
        border-color var(--ai-accent)
        color var(--ai-accent)

    &.active
        background var(--ai-accent)
        border-color var(--ai-accent)
        color #fff

    &.is-custom
        border-style dashed

        &.active
            border-style solid

    &.gm-more
        border-style dashed
        color var(--ai-accent)

.gm-ratios
    display flex
    flex-wrap wrap
    gap 6px

.gm-orientation
    display flex
    gap 6px
    margin-top 6px

    .gm-ratio
        display inline-flex
        align-items center
        gap 5px

.gm-ratio
    border 1px solid #dcdfe6
    background #fff
    color #606266
    border-radius 6px
    padding 5px 10px
    font-size 12px
    line-height 1
    cursor pointer
    transition all .15s ease

    &:hover
        border-color var(--ai-accent)
        color var(--ai-accent)

    &.active
        background var(--ai-accent)
        border-color var(--ai-accent)
        color #fff

.gm-cost
    margin-top 20px
    padding 12px 20px
    background #f7f4ff
    border-radius 6px

    &__row
        display flex
        align-items baseline
        justify-content space-between

        &--muted
            margin-top 6px

    &__line
        display flex
        align-items center
        gap 6px

        .el-icon-info
            color #b8a9e8
            cursor help

    &__label
        font-size 13px
        color #303133

    &__est
        font-size 11px
        color #909399

    &__amount
        font-size 20px
        font-weight 700
        color var(--ai-accent)

    &__amount--muted
        font-size 14px
        font-weight 600
        color #8a8a99
</style>
